import { PresentationAPI } from 'capacitor-presentation-api';
import { Capacitor } from '@capacitor/core';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { useConfigStore } from '@/stores/config';
import { watch } from 'vue';
import { getCDSDeviceId } from '@/capacitors/utils';
import { WorkManager } from '@/workers/work.manager';
import { logInfo } from '@/utils/logger';

export class PresentationService {
  private static instance: PresentationService;
  private enabled = false;
  private available = false;
  private watching = false; // Prevent duplicate config watchers
  private retryCount = 0;
  private enabling = false; // Prevent concurrent enable() calls
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 3000;
  private readonly CDS_CHECK_INTERVAL = 15 * 60 * 1000;

  // stable reference for WorkManager — re-probes display when unavailable, otherwise verifies CDS window is on secondary
  private cdsHealthCheck = async () => {
    if (!this.enabled) return;
    if (!this.available) {
      this.available = await this.checkDisplays();
      logInfo('[CDS] re-probe', { success: this.available });
      if (this.available) await this.showWelcome();
      return;
    }
    if (!this.isElectron()) return;
    const ep = (window as any).electronPresentation;
    const isOpen = await ep.isWindowOpen();
    const isOnSecondary = isOpen && await ep.isOnSecondaryDisplay();
    logInfo('[CDS] health check', { isOpen, isOnSecondary });
    if (!isOpen || !isOnSecondary) {
      if (isOpen) await ep.close(); // force close so openURL recreates on secondary
      await this.showWelcome();
    }
  };

  private constructor() {}

  public static getInstance(): PresentationService {
    if (!PresentationService.instance) PresentationService.instance = new PresentationService();
    return PresentationService.instance;
  }

  private isSupported(): boolean {
    const platform = Capacitor.getPlatform();
    return platform !== 'ios';
  }

  private isEnabledInSettings(): boolean {
    const configStore = useConfigStore();
    return configStore.appConfig?.config?.cds?.enabled ?? false;
  }

  private isElectron(): boolean {
    return !!(window as any).electronPresentation;
  }

  private isEmployeeLoggedIn(): boolean {
    const staffData = secureStorage.getItem('_STAFF_');
    return !!staffData;
  }

  private async checkDisplays(): Promise<boolean> {
    try {
      if (this.isElectron()) {
        const secondary = await (window as any).electronPresentation.checkSecondaryDisplay();
        return !!secondary;
      } else {
        const { displays } = await PresentationAPI.getDisplays();
        return displays > 0;
      }
    } catch (e) {
      console.error('CDS display check failed', e);
      return false;
    }
  }

  // Background retry when initial detection fails (Windows may initialize displays slowly)
  private retryInBackground(): void {
    if (this.available || this.retryCount >= this.MAX_RETRIES) return;
    setTimeout(async () => {
      if (this.available) return; // Double check after delay
      this.retryCount++;
      this.available = await this.checkDisplays();
      if (this.available) { if (this.enabled) await this.showWelcome(); }
      else this.retryInBackground();
    }, this.RETRY_DELAY);
  }

  private startConfigWatch(): void {
    if (this.watching) return; // Prevent duplicate watchers
    this.watching = true;
    const configStore = useConfigStore();
    watch(
      () => configStore.appConfig?.config?.cds?.enabled,
      async (settingEnabled) => (settingEnabled ? this.enable() : this.disable())
    );
  }

  public async init(): Promise<boolean> {
    if (!this.isSupported()) { this.available = false; return false; }
    this.retryCount = 0; // Reset retry count for fresh init
    this.startConfigWatch();
    this.available = await this.checkDisplays();
    if (!this.available) this.retryInBackground();
    await this.enable(); // starts the health-check worker even if display/settings aren't ready yet
    return this.available;
  }

  // Single source of truth for the three conditions (settings, login, display) — starts the worker so a
  // not-yet-available display gets picked up later by cdsHealthCheck's re-probe
  public async enable(): Promise<void> {
    if (!this.isEnabledInSettings() || !this.isEmployeeLoggedIn() || this.enabling) return;
    this.enabling = true;
    this.enabled = true;
    WorkManager.getInstance().startCdsWorker(this.cdsHealthCheck, this.CDS_CHECK_INTERVAL);
    if (this.available) await this.showWelcome();
    this.enabling = false;
  }

  public async disable(): Promise<void> {
    if (!this.enabled) return;
    WorkManager.getInstance().stopCdsWorker();
    await this.clear();
    this.enabled = false;
  }

  public async showWelcome(): Promise<void> {
    if (!this.enabled || !this.available) return;
    const token = secureStorage.getAccessToken();
    const merchantUid = secureStorage.getItem('merchant_uid');
    const cdsDeviceId = await getCDSDeviceId();
    const cdsUrl = import.meta.env.VITE_CDS_URL || 'https://cds-dev.system.posx.ai';
    const url = `${cdsUrl}/?token=${token}&merchant_uid=${merchantUid}&cds_device_id=${cdsDeviceId}`;
    try {
      if (this.isElectron()) await (window as any).electronPresentation.openURL(url);
      else await PresentationAPI.openLink({ url });
    } catch (e) {
      console.error('CDS display failed', e);
    }
  }

  public async clear(): Promise<void> {
    if (!this.available) return;
    try {
      if (this.isElectron()) await (window as any).electronPresentation.close();
      else await PresentationAPI.openRawHtml({ htmlStr: '' });
    } catch (e) {
      console.error('CDS clear failed', e);
    }
  }

  public isEnabled(): boolean { return this.enabled; }
  public isAvailable(): boolean { return this.available; }
}
